import { Component, inject, OnInit, signal, viewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  AnimatedHeightContainerComponent,
  ModalComponent,
} from '@catch/general-ui';
import { ConstantsService, ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgSelectModule } from '@ng-select/ng-select';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import confetti from 'canvas-confetti';
import { firstValueFrom, take } from 'rxjs';
import { modalStepSlow } from '../../../assets/animations/animations';
import { OnboardingAdapter } from '../../adapters/onboarding.adapter';
import { AppState } from '../../app.reducer';
import { OnboardingTourType } from '../../enums/onboarding-tour.enum';
import { OnboardingService } from '../../services/onboarding.service';
import { TourService } from '../../services/tour.service';
import {
  loadCompanyAccount,
  loadCompanyAccountSuccess,
} from '../../store/company-account/company-account.actions';
import {
  loadFacilities,
  loadFacilitiesSuccess,
} from '../../store/facilities/facilities.actions';
import { loadSubscription } from '../../store/subscription/subscription.actions';
import {
  finishOnboarding,
  loadUserAccount,
  loadUserAccountSuccess,
} from '../../store/user-account/user-account.actions';
import { ButtonComponent } from '../button/button.component';
import { CombinedSubscriptionBookingModalComponent } from '../combined-subscription-booking-modal/combined-subscription-booking-modal.component';
import { LocationInputComponent } from '../location-input/location-input.component';

@UntilDestroy()
@Component({
  selector: 'app-onboarding-modal',
  templateUrl: './onboarding-modal.component.html',
  styleUrls: ['./onboarding-modal.component.scss'],
  animations: [modalStepSlow],
  imports: [
    ReactiveFormsModule,
    NgSelectModule,
    TranslocoPipe,
    ModalComponent,
    AnimatedHeightContainerComponent,
    LocationInputComponent,
    CombinedSubscriptionBookingModalComponent,
    ButtonComponent,
  ],
})
export class OnboardingModalComponent implements OnInit {
  private onboardingModal = viewChild<ModalComponent>('onboardingModal');
  private readonly combinedSubscriptionBookingModal =
    viewChild<CombinedSubscriptionBookingModalComponent>(
      'combinedSubscriptionBookingModal',
    );

  public step = signal<number>(1);
  public numOfSteps = signal<number>(3);
  public headquartersLabel = signal<string>('');
  private bookingInProgress = signal<boolean>(false);

  public profileForm = new FormGroup({
    firstName: new FormControl(null, [Validators.required]),
    lastName: new FormControl(null, [Validators.required]),
    companyName: new FormControl(null, [Validators.required]),
    headquartersHereId: new FormControl(null, [Validators.required]),
  });
  public profileFormSubmitAttempt = signal<boolean>(false);
  public sendingProfileForm = signal<boolean>(false);

  public companyForm = new FormGroup({
    role: new FormControl(null, [/*Validators.required*/]),
    numOfEmployees: new FormControl(null, [/*Validators.required*/]),
    industry: new FormControl(null, [/*Validators.required*/]),
  });
  public companyFormSubmitAttempt = signal<boolean>(false);
  public sendingCompanyForm = signal<boolean>(false);

  public teamForm = new FormGroup({
    sizeOfRecruitingTeam: new FormControl(null, [/*Validators.required*/]),
    numOfYearlyApplications: new FormControl(null, [/*Validators.required*/]),
  });
  public teamFormSubmitAttempt = signal<boolean>(false);
  public sendingTeamForm = signal<boolean>(false);

  public readonly constantsService = inject(ConstantsService);
  private readonly store = inject(Store<AppState>);
  private readonly actions$ = inject(Actions);
  private readonly onboardingAdapter = inject(OnboardingAdapter);
  private readonly onboardingService = inject(OnboardingService);
  private readonly tourService = inject(TourService);
  private readonly toastsService = inject(ToastsService);

  ngOnInit(): void {
    this.store.dispatch(loadCompanyAccount());
    this.store.dispatch(loadUserAccount());
    this.store.dispatch(loadFacilities());

    this.actions$
      .pipe(ofType(loadFacilitiesSuccess), untilDestroyed(this))
      .subscribe((facilities) => {
        const headquarters = facilities.facilities.find(
          (facility) => facility.headquarters,
        );
        if (headquarters) {
          this.headquartersLabel.set(
            `${headquarters.street ? `${headquarters.street}` : ''}${headquarters.houseNumber ? ` ${headquarters.houseNumber}` : ''}${headquarters.street || headquarters.houseNumber ? ', ' : ''}${headquarters.postalCode ?? ''}${headquarters.postalCode ? ' ' : ''}${headquarters.city}`,
          );
          this.profileForm.patchValue({
            headquartersHereId: headquarters.hereId,
          });
        }
      });
    this.actions$
      .pipe(ofType(loadCompanyAccountSuccess), take(1), untilDestroyed(this))
      .subscribe((company) => {
        this.companyForm.patchValue({
          numOfEmployees: company.account.numberOfEmployees,
          industry: company.account.industry,
        });
        this.profileForm.patchValue({
          companyName: company.account.companyName,
        });
        this.teamForm.patchValue({
          numOfYearlyApplications: company.account.numOfYearlyApplications,
          sizeOfRecruitingTeam: company.account.sizeOfRecruitingTeam,
        });
      });
    this.actions$
      .pipe(ofType(loadUserAccountSuccess), take(1), untilDestroyed(this))
      .subscribe((user) => {
        this.profileForm.patchValue({
          firstName: user.account.firstName,
          lastName: user.account.lastName,
        });
        this.companyForm.patchValue({
          role: user.account.companyRole,
        });
        if (user.account.triggerOnboardingDataCompletion) {
          setTimeout(() => {
            this.start();
          }, 500);
        } else if (
          this.onboardingService.shouldResumeSubscriptionOnboarding(
            user.account,
          )
        ) {
          this.openSubscriptionBooking();
        }
      });
    this.onboardingModal()
      .onOpen.pipe(untilDestroyed(this))
      .subscribe(() => {
        window.onbeforeunload = (e) => {
          e.preventDefault();
        };
      });
    this.onboardingModal()
      .onClose.pipe(untilDestroyed(this))
      .subscribe(() => {
        window.onbeforeunload = null;
      });
  }

  /**
   * Opens the phase-1 onboarding modal.
   * @returns {void}
   */
  public start(): void {
    this.onboardingModal().open();
  }

  async next(submit: boolean = true): Promise<void> {
    if (
      (this.step() !== 1 && this.step() !== 2) ||
      (this.step() === 1 && (!submit || (await this.onProfileFormSubmit()))) ||
      (this.step() === 2 && (!submit || (await this.onCompanyFormSubmit())))
    ) {
      this.step.update((value) => Math.min(value + 1, this.numOfSteps()));
    }
  }

  prev(): void {
    this.step.update((value) => Math.max(value - 1, 1));
  }

  skip(): void {
    if (this.step() < this.numOfSteps()) {
      this.next(false);
    } else {
      this.finish(false);
    }
  }

  /**
   * Completes phase 1 and opens subscription booking.
   * @param {boolean} submit - Whether the team form should be submitted
   * @returns {Promise<void>}
   */
  async finish(submit: boolean = true): Promise<void> {
    const teamFormValid = !submit || (await this.onTeamFormSubmit());
    if (!teamFormValid) return;
    this.store.dispatch(finishOnboarding());
    this.store.dispatch(loadCompanyAccount());
    this.store.dispatch(loadUserAccount());
    this.store.dispatch(loadFacilities());
    this.onboardingModal().close();
    this.openSubscriptionBooking();
  }

  async onProfileFormSubmit(): Promise<boolean> {
    this.profileFormSubmitAttempt.set(true);
    if (this.profileForm.valid) {
      this.profileFormSubmitAttempt.set(false);
      if (this.profileForm.untouched) return true;
      this.sendingProfileForm.set(true);
      await firstValueFrom(
        this.onboardingAdapter.updateCompanyDetails(this.profileForm.value),
      );
      this.store.dispatch(loadFacilities());
      this.sendingProfileForm.set(false);
      return true;
    }
    this.onboardingModal().shake();
    return false;
  }

  async onCompanyFormSubmit(): Promise<boolean> {
    this.companyFormSubmitAttempt.set(true);
    if (this.companyForm.valid) {
      this.companyFormSubmitAttempt.set(false);
      if (this.companyForm.untouched) return true;
      this.sendingCompanyForm.set(true);
      await firstValueFrom(
        this.onboardingAdapter.updateUserDetails(this.companyForm.value),
      );
      this.sendingCompanyForm.set(false);
      return true;
    }
    this.onboardingModal().shake();
    return false;
  }

  async onTeamFormSubmit(): Promise<boolean> {
    this.teamFormSubmitAttempt.set(true);
    if (this.teamForm.valid) {
      this.teamFormSubmitAttempt.set(false);
      if (this.teamForm.untouched) return true;
      this.sendingTeamForm.set(true);
      await firstValueFrom(
        this.onboardingAdapter.updateTeamAccount(this.teamForm.value),
      );
      this.sendingTeamForm.set(false);
      return true;
    }
    this.onboardingModal().shake();
    return false;
  }

  getProgressBarWidth(): number {
    return ((this.step() - 1) * 100) / (this.numOfSteps() - 1);
  }

  /**
   * Completes onboarding after the booking modal was booked or dismissed.
   * @returns {void}
   */
  onSubscriptionBookingClosed(): void {
    this.completeOnboarding();
  }

  /**
   * Opens the combined subscription booking modal.
   * @returns {void}
   */
  private openSubscriptionBooking(): void {
    this.store.dispatch(loadSubscription());
    this.onboardingModal()?.close();
    setTimeout(
      () =>
        this.combinedSubscriptionBookingModal()?.open({
          allowUnchangedSelection: true,
        }),
      0,
    );
  }

  /**
   * Marks onboarding as complete on the backend, then runs shared UI cleanup.
   * @returns {void}
   */
  private completeOnboarding(): void {
    if (this.bookingInProgress()) {
      return;
    }
    this.bookingInProgress.set(true);
    this.onboardingAdapter
      .completeOnboarding()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => this.onOnboardingCompleted(),
        error: () => {
          this.bookingInProgress.set(false);
        },
      });
  }

  /**
   * Shared UI cleanup after onboarding has been marked complete on the backend.
   * @returns {void}
   */
  private onOnboardingCompleted(): void {
    this.store.dispatch(loadSubscription());
    this.store.dispatch(loadUserAccount());
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#6E8BEF', '#77CEC7', '#FD9326'],
    });
    this.toastsService.info('company-tool.onboarding.modal.toasts.welcome');
    this.tourService.startTour(OnboardingTourType.DASHBOARD);
    this.bookingInProgress.set(false);
  }
}
